import React from 'react';
import { DEMAND_HOURS, DAY_NAMES, formatHourLabel } from '../../hooks/useDemandPlanning';

const DemandPlanningTab = ({
  loading,
  demandPlanning
}) => {
  const {
    maxCapacity,
    setMaxCapacity,
    demandSelectedDay,
    setDemandSelectedDay,
    demandHasChanges,
    setDemandHasChanges,
    avgLengthOfStay,
    setAvgLengthOfStay,
    demandViewMode,
    setDemandViewMode,
    saveDemandData,
    getEffectiveDemandData,
    getEffectiveOnlineOrderData,
    hasWeekOverride,
    hasOnlineOrderWeekOverride,
    weekHasOverrides,
    clearWeekOverrides,
    updateDemandValue,
    updateOnlineOrderValue,
    copyDayToAll,
    copyToWeekdays,
    copyToWeekends,
    getDayTotalCovers,
    getWeekTotalCovers,
    getDayTotalOnlineOrders,
    getWeekTotalOnlineOrders,
    getPeakHour,
    getPeakOnlineOrderHour,
    getConcurrentCovers,
    getPeakConcurrent,
    goToPrevDemandWeek,
    goToNextDemandWeek,
    goToCurrentDemandWeek,
    formatDemandWeekLabel
  } = demandPlanning;

  return (
    <div className="demand-planning-view">
      <div className="demand-header">
        <div className="demand-title-section">
          <h3>{demandViewMode === 'template' ? 'Default Demand Template' : 'Weekly Demand Override'}</h3>
          <p>{demandViewMode === 'template'
            ? 'Set the default demand curve that applies to all weeks'
            : `Override demand for the week of ${formatDemandWeekLabel()}`}
          </p>
        </div>
        <div className="demand-actions">
          {demandHasChanges && <span className="unsaved-indicator">Unsaved changes</span>}
          <button
            className="btn btn-primary"
            onClick={saveDemandData}
            disabled={loading || !demandHasChanges}
          >
            {loading ? 'Saving...' : 'Save All Changes'}
          </button>
        </div>
      </div>

      <div className="demand-view-mode">
        <div className="view-mode-tabs">
          <button
            className={`view-mode-tab ${demandViewMode === 'template' ? 'active' : ''}`}
            onClick={() => setDemandViewMode('template')}
          >
            Default Template
          </button>
          <button
            className={`view-mode-tab ${demandViewMode === 'week' ? 'active' : ''}`}
            onClick={() => setDemandViewMode('week')}
          >
            Weekly Override
          </button>
        </div>

        {demandViewMode === 'week' && (
          <div className="demand-week-navigation">
            <button className="btn btn-secondary btn-sm" onClick={goToPrevDemandWeek}>&lt;</button>
            <span className="demand-week-label">{formatDemandWeekLabel()}</span>
            <button className="btn btn-secondary btn-sm" onClick={goToNextDemandWeek}>&gt;</button>
            <button className="btn btn-secondary btn-sm" onClick={goToCurrentDemandWeek}>This Week</button>
            {weekHasOverrides() && (
              <button className="btn btn-danger btn-sm" onClick={clearWeekOverrides}>
                Clear Overrides
              </button>
            )}
          </div>
        )}

        {demandViewMode === 'week' && weekHasOverrides() && (
          <div className="week-override-indicator">
            This week has custom overrides. Values shown differ from the default template.
          </div>
        )}
      </div>

      <div className="demand-settings">
        <div className="capacity-setting">
          <label>Max Seating Capacity</label>
          <input
            type="number"
            value={maxCapacity}
            onChange={(e) => {
              setMaxCapacity(Math.max(1, parseInt(e.target.value) || 1));
              setDemandHasChanges(true);
            }}
            min="1"
          />
          <span className="setting-hint">Maximum covers at any given time</span>
        </div>
        <div className="length-of-stay-setting">
          <label>Avg. Length of Stay: {avgLengthOfStay} min</label>
          <input
            type="range"
            min="30"
            max="180"
            step="15"
            value={avgLengthOfStay}
            onChange={(e) => {
              setAvgLengthOfStay(parseInt(e.target.value));
              setDemandHasChanges(true);
            }}
            className="stay-slider"
          />
          <div className="slider-labels">
            <span>30 min</span>
            <span>180 min</span>
          </div>
          <span className="setting-hint">Average time a party occupies a table</span>
        </div>
      </div>

      <div className="demand-summary-cards">
        <div className="summary-card">
          <span className="summary-value">{getWeekTotalCovers()}</span>
          <span className="summary-label">Weekly Covers</span>
        </div>
        <div className="summary-card">
          <span className="summary-value">{getDayTotalCovers(demandSelectedDay)}</span>
          <span className="summary-label">{demandSelectedDay.charAt(0).toUpperCase() + demandSelectedDay.slice(1)} Covers</span>
        </div>
        <div className="summary-card">
          <span className="summary-value">
            {getPeakHour(demandSelectedDay).hour ? formatHourLabel(getPeakHour(demandSelectedDay).hour) : '-'}
          </span>
          <span className="summary-label">Peak Arrivals ({getPeakHour(demandSelectedDay).value})</span>
        </div>
        <div className="summary-card highlight">
          <span className="summary-value">{getPeakConcurrent(demandSelectedDay).value}</span>
          <span className="summary-label">Peak Seats Occupied</span>
          <span className="summary-hint">{getPeakConcurrent(demandSelectedDay).value > maxCapacity ? 'Over capacity!' : `${Math.round((getPeakConcurrent(demandSelectedDay).value / maxCapacity) * 100)}% of max`}</span>
        </div>
      </div>

      <div className="demand-summary-cards online-orders-summary">
        <div className="summary-card online">
          <span className="summary-value">{getWeekTotalOnlineOrders()}</span>
          <span className="summary-label">Weekly Online Orders</span>
        </div>
        <div className="summary-card online">
          <span className="summary-value">{getDayTotalOnlineOrders(demandSelectedDay)}</span>
          <span className="summary-label">{demandSelectedDay.charAt(0).toUpperCase() + demandSelectedDay.slice(1)} Online</span>
        </div>
        <div className="summary-card online">
          <span className="summary-value">
            {getPeakOnlineOrderHour(demandSelectedDay).hour ? formatHourLabel(getPeakOnlineOrderHour(demandSelectedDay).hour) : '-'}
          </span>
          <span className="summary-label">Peak Online ({getPeakOnlineOrderHour(demandSelectedDay).value})</span>
        </div>
        <div className="summary-card online highlight">
          <span className="summary-value">{getDayTotalCovers(demandSelectedDay) + getDayTotalOnlineOrders(demandSelectedDay)}</span>
          <span className="summary-label">Total Kitchen Demand</span>
          <span className="summary-hint">In-person + Online orders</span>
        </div>
      </div>

      <div className="demand-day-selector">
        {DAY_NAMES.map(day => (
          <button
            key={day}
            className={`day-tab ${demandSelectedDay === day ? 'active' : ''}`}
            onClick={() => setDemandSelectedDay(day)}
          >
            <span className="day-tab-name">{day.charAt(0).toUpperCase() + day.slice(1, 3)}</span>
            <span className="day-tab-total">{getDayTotalCovers(day)}</span>
          </button>
        ))}
      </div>

      <div className="demand-editor">
        <div className="demand-editor-header">
          <h3 className="editing-day-title">
            {demandSelectedDay.charAt(0).toUpperCase() + demandSelectedDay.slice(1)}
            {demandViewMode === 'week' && (() => {
              const dayIndex = DAY_NAMES.indexOf(demandSelectedDay);
              const weekStart = new Date(demandPlanning.demandWeekStart);
              const targetDate = new Date(weekStart);
              targetDate.setDate(weekStart.getDate() + dayIndex);
              return `, ${targetDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
            })()}
          </h3>
          <span className="editing-day-subtitle">
            {demandViewMode === 'template'
              ? 'Editing default arrival times for this day'
              : 'Editing override for this specific date'}
          </span>
        </div>
        <div className="demand-copy-actions">
          <span>Copy this pattern to:</span>
          <button className="btn btn-sm btn-secondary" onClick={() => copyToWeekdays(demandSelectedDay)}>
            Weekdays
          </button>
          <button className="btn btn-sm btn-secondary" onClick={() => copyToWeekends(demandSelectedDay)}>
            Weekends
          </button>
          <button className="btn btn-sm btn-secondary" onClick={() => copyDayToAll(demandSelectedDay)}>
            All Days
          </button>
        </div>

        <div className="demand-chart">
          <div className="chart-legend">
            <span className="legend-item arrivals"><span className="legend-color"></span>Arrivals</span>
            <span className="legend-item occupied"><span className="legend-color"></span>Seats Occupied</span>
            <span className="legend-item capacity"><span className="legend-color"></span>Max Capacity</span>
          </div>
          <div className="chart-y-axis">
            <span>{maxCapacity}</span>
            <span>{Math.round(maxCapacity * 0.75)}</span>
            <span>{Math.round(maxCapacity * 0.5)}</span>
            <span>{Math.round(maxCapacity * 0.25)}</span>
            <span>0</span>
          </div>
          <div className="chart-bars">
            {DEMAND_HOURS.map(hour => {
              const effectiveData = getEffectiveDemandData();
              const arrivals = effectiveData[demandSelectedDay]?.[hour] || 0;
              const concurrent = getConcurrentCovers(demandSelectedDay, hour);
              const arrivalsPercent = maxCapacity > 0 ? (arrivals / maxCapacity) * 100 : 0;
              const concurrentPercent = maxCapacity > 0 ? (concurrent / maxCapacity) * 100 : 0;
              const isOverCapacity = concurrent > maxCapacity;
              const isOverride = hasWeekOverride(demandSelectedDay, hour);

              return (
                <div key={hour} className={`chart-bar-container ${isOverride ? 'has-override' : ''}`}>
                  <div className="chart-bar-wrapper">
                    <div
                      className={`chart-bar concurrent ${isOverCapacity ? 'over-capacity' : ''}`}
                      style={{ height: `${Math.min(concurrentPercent, 100)}%` }}
                      title={`Seats occupied: ${concurrent}`}
                    >
                      <span className="bar-value concurrent-value">{concurrent}</span>
                    </div>
                    <div
                      className={`chart-bar arrivals ${isOverride ? 'override' : ''}`}
                      style={{ height: `${Math.min(arrivalsPercent, 100)}%` }}
                      title={`Arrivals: ${arrivals}${isOverride ? ' (override)' : ''}`}
                    >
                      {arrivals > 0 && <span className="bar-value arrivals-value">{arrivals}</span>}
                    </div>
                  </div>
                  <span className="bar-label">{formatHourLabel(hour, true)}</span>
                </div>
              );
            })}
          </div>
          <div className="capacity-line" style={{ bottom: `calc(${Math.min(100, 100)}% + 30px)` }}></div>
        </div>

        <div className="demand-inputs">
          <div className="inputs-header">
            <span>Time</span>
            <span>In-Person</span>
            <span>Online Orders</span>
            <span>Seats Occupied</span>
            <span>Kitchen Load</span>
          </div>
          <div className="demand-inputs-body">
            {DEMAND_HOURS.map((hour, index) => {
              const effectiveData = getEffectiveDemandData();
              const onlineData = getEffectiveOnlineOrderData();
              const arrivals = effectiveData[demandSelectedDay]?.[hour] || 0;
              const onlineOrders = onlineData[demandSelectedDay]?.[hour] || 0;
              const concurrent = getConcurrentCovers(demandSelectedDay, hour);
              const kitchenLoad = arrivals + onlineOrders;
              const percentage = maxCapacity > 0 ? Math.round((concurrent / maxCapacity) * 100) : 0;
              const isHourStart = index % 4 === 0;
              const isOverride = hasWeekOverride(demandSelectedDay, hour);
              const isOnlineOverride = hasOnlineOrderWeekOverride(demandSelectedDay, hour);

              return (
                <div key={hour} className={`demand-input-row ${isHourStart ? 'hour-start' : ''} ${isOverride || isOnlineOverride ? 'has-override' : ''}`}>
                  <span className="input-hour">
                    {formatHourLabel(hour)}
                    {(isOverride || isOnlineOverride) && <span className="override-badge">*</span>}
                  </span>
                  <input
                    type="number"
                    value={arrivals}
                    onChange={(e) => updateDemandValue(demandSelectedDay, hour, e.target.value)}
                    min="0"
                    max={maxCapacity * 2}
                    className={`in-person-input ${isOverride ? 'override-input' : ''}`}
                    title="In-person arrivals"
                  />
                  <input
                    type="number"
                    value={onlineOrders}
                    onChange={(e) => updateOnlineOrderValue(demandSelectedDay, hour, e.target.value)}
                    min="0"
                    className={`online-order-input ${isOnlineOverride ? 'override-input' : ''}`}
                    title="Online orders"
                  />
                  <span className={`seats-occupied ${concurrent > maxCapacity ? 'over-capacity' : ''}`}>
                    {concurrent}
                  </span>
                  <span className="kitchen-load" title="Total demand for kitchen staff">
                    {kitchenLoad}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="demand-weekly-overview">
        <h4>Weekly Overview (Seats Occupied)</h4>
        <div className="weekly-heatmap">
          <div className="heatmap-header">
            <span></span>
            {DEMAND_HOURS.filter((_, i) => i % 4 === 0).map(hour => (
              <span key={hour} className="heatmap-hour-group">{formatHourLabel(hour, true)}</span>
            ))}
          </div>
          {DAY_NAMES.map(day => (
            <div key={day} className="heatmap-row">
              <span className="heatmap-day">{day.charAt(0).toUpperCase() + day.slice(1, 3)}</span>
              {DEMAND_HOURS.map((hour, i) => {
                const concurrent = getConcurrentCovers(day, hour);
                const intensity = maxCapacity > 0 ? Math.min(concurrent / maxCapacity, 1) : 0;
                const isHourStart = i % 4 === 0;
                const isOverride = hasWeekOverride(day, hour);
                return (
                  <div
                    key={hour}
                    className={`heatmap-cell ${isHourStart ? 'hour-start' : ''} ${isOverride ? 'has-override' : ''}`}
                    style={{
                      backgroundColor: isOverride
                        ? `rgba(255, 193, 7, ${Math.max(0.3, intensity)})`
                        : `rgba(220, 53, 69, ${intensity})`,
                      color: intensity > 0.5 ? 'white' : '#333'
                    }}
                    title={`${day} ${formatHourLabel(hour)}: ${concurrent} seated${isOverride ? ' (override)' : ''}`}
                  >
                    {concurrent > 0 ? concurrent : ''}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DemandPlanningTab;
